import { memoUrlParsed } from '../../tppServices/cacheService';
import { getConfig } from '../../config/portal';
import { generatePath, matchPath } from 'react-router';
import { getBoatConstantsFromI18n } from '../../tppServices/translations/constants';
import { parseProductsSearchParams } from './products';
import { getDefaultI18Service, getFormatMessageFunction } from '../../tppServices/tppDi';
import { getOverridableBoats, getOverridableRoutes } from '../language';
import { getMessages } from '../../tppServices/translations/messages';
import isEmpty from 'lodash/isEmpty';
import { normalizeString as normalizeMakeModel } from '@dmm/lib-common/lib/formatting';
import { getBoatsConstants, getLocalizedCountryCodes } from '../../constants/boats';
import get from 'lodash/get';
import { logger } from '../logger';
import flatten from 'lodash/flatten';
import { getCountryURLFromCode, getSubdivisionURLFromCode, updateLocationPathParams } from './shared';
import has from 'lodash/has';

import { generateMakeAndModelPathParam, hasMakeModel } from './stateUrlCreators';

import { getDealerGalleryPattern, getSearchUrlPattern } from './patternFinders';
import { parseBoatSearchParams } from './constantsParsers';

/**
 * Parses the search parameters from a given URL.
 * If we call this function with the same URL, it will return the cached result.
 * @param url the URL to parse
 * @param isBranded indicates if the URL is branded
 * @param customUom optional custom unit of measure to use in the parsing
 * @param config optional configuration object, if not provided it will use the default configuration
 * @param i18nService optional i18n service to use for translations, if not provided it will use the default i18n service
 * @returns {{} an object containing the parsed search parameters})}
 */
export const parseSearchParams = ( url, isBranded = false, customUom, config, i18nService) => {
  if (!url) {
    return {};
  }
  const alreadyParsed = memoUrlParsed(url, undefined, isBranded, customUom);
  if (alreadyParsed) {
    return alreadyParsed;
  }
  // istanbul ignore next
  if (!config) {
    config = getConfig();
  }

  const searchPattern = getSearchUrlPattern(url, isBranded);
  const matched = matchPath(decodeURI(url), searchPattern);
  if (!matched) {
    return {};
  }
  // params contains the elements of the URL that match the pattern
  let { params } = matched;
  const boatConstants = getBoatConstantsFromI18n(i18nService);
  let parsedParams;
  switch (searchPattern) {
    case boatConstants.MOORINGS_SEARCH_URL_PATTERN: {
      parsedParams = parseProductsSearchParams( boatConstants, params, config, customUom, i18nService );
      break;
    }
    default:
      parsedParams = parseBoatSearchParams( boatConstants, params, config, customUom, i18nService );
  }

  memoUrlParsed(url, parsedParams, isBranded, customUom);
  return parsedParams;
};

// TODO: rewrite
export const boatsUrlGenerate = (
  changes,
  urlProps,
  newSearch,
  overrideLocale,
  i18Service
) => {
  if (!i18Service) {
    i18Service = getDefaultI18Service();
  }
  // locale is overriden only in translating location
  // We are not doing that anymore.
  // TODO: review if that is true
  const config = getOverridableRoutes(overrideLocale, i18Service);
  const bt = getOverridableBoats(overrideLocale, i18Service);
  const t = getFormatMessageFunction(i18Service, overrideLocale);
  const messages = getMessages();
  const tr = (message, params) => t(message, params);
  let removeLastSlash = false;
  let initialParams = {
    ...urlProps,
    ...changes
  };

  let finalParams = {};

  if (initialParams.modal) {
    delete initialParams.modal;
  }

  // Handle placeId first, as it takes precedence over latlon
  if (initialParams.placeId) {
    finalParams.placeId = `${config.SEARCH_URL_ITEM_PLACEID}-${initialParams.placeId}`;
    delete initialParams.placeId; // Remove so it's not processed again
    // Ensure latlon and other specific location fields are not also processed if placeId is present
    delete initialParams.latlon;
    delete initialParams.country;
    delete initialParams.region;
    delete initialParams.subdivision;
    delete initialParams.city;
    // Radius is handled separately and can coexist with placeId
  } else if (initialParams.latlon) {
    // latlon is only processed if placeId is not present
    finalParams.latlon = `${config.SEARCH_URL_ITEM_LATLON}-${initialParams.latlon}`; // initialParams.latlon is the original Base64 string
    delete initialParams.latlon;
    // Clear other specific location fields if latlon is used
    delete initialParams.country;
    delete initialParams.region;
    delete initialParams.subdivision;
    delete initialParams.city;
  }

  if (initialParams.makeModel) {
    if (!isEmpty(initialParams.makeModel)) {
      let seperateMakeModel = generateMakeAndModelPathParam(
        initialParams.makeModel
      );
      if (seperateMakeModel) {
        finalParams.make = `${config.SEARCH_URL_ITEM_MAKE}-${normalizeMakeModel(
          seperateMakeModel.make,
          getBoatsConstants().ALLOWED_CHARACTERS_IN_URL
        )}`;
        if (seperateMakeModel.model) {
          if (initialParams.modelRange) {
            delete initialParams.modelRange;
          }
          finalParams.model = `${
            config.SEARCH_URL_ITEM_MODEL
          }-${normalizeMakeModel(
            seperateMakeModel.model,
            getBoatsConstants().ALLOWED_CHARACTERS_IN_URL
          )}`;
        }
      } else {
        const isMakeModel = hasMakeModel(initialParams.makeModel);
        if (!isMakeModel) {
          const makes = Object.keys(initialParams.makeModel);
          const flattenMakes = makes.flat();
          finalParams.make = `${
            config.SEARCH_URL_ITEM_MAKE
          }-${flattenMakes.join('+')}`;
        } else {
          const models = Object.keys(initialParams.makeModel)
            .map((makeName) => {
              const models = Object.values(
                get(initialParams.makeModel, makeName, [])
              );
              if (models.length) {
                return models.map((modelName) => {
                  return `${makeName}:${modelName}`;
                });
              }
              return makeName;
            })
            .filter((makeModel) => !!makeModel);
          const flattenModels = models.flat();
          finalParams.makeModel = `${
            config.SEARCH_URL_ITEM_MAKEMODEL
          }-${flattenModels.join('+')}`;
        }
      }
    } else {
      delete initialParams.modelRange;
    }
    delete initialParams.makeModel;
  }

  if (initialParams.sort) {
    try {
      const [sortBy, sortOrder] = initialParams.sort.split(':');
      const translatedSort = `${tr(messages.search[sortBy])}:${tr(
        messages.sortFacet[sortOrder]
      )}`;
      finalParams.sort = `${config.SEARCH_URL_ITEM_SORT}-${translatedSort}`;
    } catch (e) {
      logger.warn('Not valid sorting parameter:' + initialParams.sort);
    }
    delete initialParams.sort;
  }

  if (initialParams.condition) {
    if (initialParams.condition !== 'any') {
      const translatedCondition = tr(
        messages.conditionFacet[initialParams.condition]
      );
      finalParams.condition = `${config.SEARCH_URL_ITEM_CONDITION}-${translatedCondition}`;
    }
    delete initialParams.condition;
  }

  if (initialParams.keyword) {
    if (
      initialParams.searchKeywordParamFormat &&
      initialParams.searchKeywordParamFormat === 'query-string'
    ) {
      removeLastSlash = true;
      finalParams.keyword = `?${config.SEARCH_URL_ITEM_KEYWORD}=${initialParams.keyword}`;
    } else {
      finalParams.keyword = `${config.SEARCH_URL_ITEM_KEYWORD}-${initialParams.keyword}`;
    }
    delete initialParams.searchKeywordParamFormat;
    delete initialParams.keyword;
  }

  if (!isEmpty(initialParams.year)) {
    let year = [initialParams.year.min, bt.DEFAULT_MAX_YEAR + 1];

    if (
      initialParams.year.max &&
      initialParams.year.max < bt.DEFAULT_MAX_YEAR + 1
    ) {
      year[1] = initialParams.year.max;
    }

    finalParams.year = config.SEARCH_URL_ITEM_YEAR + '-' + year.join(',');

    delete initialParams.year;
  }

  if (initialParams.fuelType) {
    if (initialParams.fuelType.length > 0) {
      finalParams.fuelType = `${
        config.SEARCH_URL_ITEM_FUEL
      }-${initialParams.fuelType
        .map((fuel) => {
          return tr(messages.fuelTypeFacet[fuel]);
        })
        .join('+')}`;
    }
    delete initialParams.fuelType;
  }

  if (initialParams.hullMaterial) {
    if (initialParams.hullMaterial.length > 0) {
      finalParams.hullMaterial = `${
        config.SEARCH_URL_ITEM_HULL
      }-${initialParams.hullMaterial
        .map((material) => {
          return tr(messages.hullMaterialFacet[material]);
        })
        .join('+')}`;
    }
    delete initialParams.hullMaterial;
  }

  if (!isEmpty(initialParams.length)) {
    if (
      initialParams.length.max &&
      initialParams.length.max !== bt.SLIDER_MAX_VALUE
    ) {
      finalParams.length =
        config.SEARCH_URL_ITEM_LENGTH +
        `-${initialParams.length.min || 0},${initialParams.length.max}`;
    } else {
      finalParams.length = `${config.SEARCH_URL_ITEM_LENGTH}-${
        initialParams.length.min || 0
      }`;
    }
    delete initialParams.length;
  }

  if (!isEmpty(initialParams.engineOneHours)) {
    if (
      initialParams.engineOneHours.max &&
      initialParams.engineOneHours.max !== bt.SLIDER_MAX_VALUE
    ) {
      finalParams.engineOneHours =
        config.SEARCH_URL_ITEM_ENGINE_HOURS +
        `-${initialParams.engineOneHours.min || 0},${initialParams.engineOneHours.max}`;
    } else {
      finalParams.engineOneHours = `${config.SEARCH_URL_ITEM_ENGINE_HOURS}-${
        initialParams.engineOneHours.min || 0
      }`;
    }
    delete initialParams.engineOneHours;
  }

  if (initialParams.engine) {
    if (
      Array.isArray(initialParams.engine) &&
      initialParams.engine.length > 0
    ) {
      finalParams.engine = `${
        config.SEARCH_URL_ITEM_ENGINE
      }-${initialParams.engine
        .map((type) => {
          return type;
        })
        .join('+')}`;
    }
    delete initialParams.engine;
  }

  if (!isEmpty(initialParams.multiFacetedBoatTypeClass)) {
    let typeList = [];
    let classList = [];

    if (initialParams.multiFacetedBoatTypeClass.power) {
      typeList.push('power');
      let filtered = initialParams.multiFacetedBoatTypeClass.power.filter(
        (item) => item !== 'power-all'
      );
      if (filtered.length) {
        classList = [...classList, filtered];
      }
    }
    if (initialParams.multiFacetedBoatTypeClass.sail) {
      typeList.push('sail');
      let filtered = initialParams.multiFacetedBoatTypeClass.sail.filter(
        (item) => item !== 'sail-all'
      );
      if (filtered.length) {
        classList = [...classList, filtered];
      }
    }
    if (initialParams.multiFacetedBoatTypeClass.unpowered) {
      typeList.push('unpowered');
      let filtered = initialParams.multiFacetedBoatTypeClass.unpowered.filter(
        (item) => item !== 'unpowered-all'
      );
      if (filtered.length) {
        classList = [...classList, filtered];
      }
    }
    if (typeList.length) {
      finalParams.types = `${config.SEARCH_URL_ITEM_TYPES}-${typeList
        .map((searchType) => {
          return tr(messages.typesFacet[searchType]);
        })
        .join('+')}`;
    }
    if (classList.length) {
      const translatedClasses = flatten(classList)
        .map((searchClass) => {
          return messages.classFacet[searchClass]
            ? tr(messages.classFacet[searchClass])
            : searchClass;
        })
        .join('+');
      finalParams.classes = `${config.SEARCH_URL_ITEM_CLASS}-${translatedClasses}`;
    }
    delete initialParams.multiFacetedBoatTypeClass;
    delete initialParams.group;
  }

  if (!isEmpty(initialParams.price)) {
    if (
      initialParams.price.max &&
      initialParams.price.max !== bt.SLIDER_MAX_VALUE
    ) {
      finalParams.price = `${config.SEARCH_URL_ITEM_PRICE}-${
        initialParams.price.min || 0
      },${initialParams.price.max}`;
    } else {
      finalParams.price = `${config.SEARCH_URL_ITEM_PRICE}-${
        initialParams.price.min || 0
      }`;
    }
    delete initialParams.price;
  }

  if (!isEmpty(initialParams.currency)) {
    const currentCurrency = initialParams.currency;
    finalParams.currency = `currency-${currentCurrency.toLowerCase()}`;
    delete initialParams.currency;
  }

  if (!isEmpty(initialParams.region)) {
    if (initialParams.region !== 'all') {
      const translatedRegion =
        initialParams.country === 'US' &&
        messages.regionFacet[initialParams.region]
          ? tr(messages.regionFacet[initialParams.region])
          : initialParams.region.toLowerCase();
      finalParams.region = `${config.SEARCH_URL_ITEM_REGION}-${translatedRegion}`;
    }
    delete initialParams.region;
  }

  if (!isEmpty(initialParams.country)) {
    if (initialParams.country !== 'all') {
      finalParams.country = getCountryURLFromCode(
        initialParams.country.toLowerCase(),
        overrideLocale
      );
    }
    delete initialParams.country;
  }

  if (!isEmpty(initialParams.subdivision)) {
    if (initialParams.subdivision !== 'all') {
      const localizedCountryCodes = getLocalizedCountryCodes();
      const country = (
        urlProps && urlProps.country
          ? urlProps.country
          : changes.country
            ? changes.country
            : get(getConfig(), 'country')
      ).toLowerCase();
      const subdivision = initialParams.subdivision.toLowerCase();
      const subdivisionURLCode = getSubdivisionURLFromCode(
        country,
        subdivision,
        overrideLocale
      );
      const useLocationSpecificSubdivisions = get(
        getConfig(),
        'supports.useLocationSpecificSubdivisions',
        false
      );
      if (useLocationSpecificSubdivisions && country === 'us') {
        finalParams.subdivision = `${tr(
          messages.search.subdivisionUS
        )}-${subdivisionURLCode}`;
      } else if (useLocationSpecificSubdivisions && country === 'ca') {
        finalParams.subdivision = `${tr(
          messages.search.subdivisionCA
        )}-${subdivisionURLCode}`;
      } else if (
        !useLocationSpecificSubdivisions &&
        localizedCountryCodes.includes(country)
      ) {
        const msg = messages.search[`subdivision${country.toUpperCase()}`];
        const localizedSubdivision = tr(msg);
        finalParams.subdivision = `${localizedSubdivision}-${subdivisionURLCode}`;
      } else {
        finalParams.subdivision = `${config.SEARCH_URL_ITEM_SUBDIVISION}-${subdivisionURLCode}`;
      }
    }
    delete initialParams.subdivision;
  }

  if (initialParams.city) {
    if (initialParams.city.length > 0) {
      finalParams.city = `${
        config.SEARCH_URL_ITEM_CITY
      }-${initialParams.city.join('+')}`;
    }
    delete initialParams.city;
  }

  if (!isEmpty(initialParams.worldRegion)) {
    if (!finalParams.country) {
      const translatedWorldRegion = tr(
        messages.worldRegionFacet[initialParams.worldRegion]
      );
      finalParams.worldRegion = `${config.SEARCH_URL_ITEM_WORLD_REGION}-${translatedWorldRegion}`;
    }
    delete initialParams.worldRegion;
  }

  if (initialParams.numberOfEngines) {
    if (initialParams.numberOfEngines > 0) {
      finalParams.numberOfEngines = `${config.SEARCH_URL_ITEM_ENGINE_NUMBER}-${initialParams.numberOfEngines}`;
    }
    delete initialParams.numberOfEngines;
  }

  if (initialParams.page) {
    if (newSearch) {
      delete initialParams.page;
      delete initialParams.pageSize;
    } else {
      if (initialParams.page !== '1' && initialParams.page !== 1) {
        finalParams.page = `${config.SEARCH_URL_ITEM_PAGE}-${initialParams.page}`;
      }
      delete initialParams.page;
      delete initialParams.pageSize;
    }
  }

  if (initialParams.forSale) {
    if (initialParams.forSale !== 'all') {
      finalParams.by = `${config.SEARCH_URL_ITEM_BY}-${tr(
        messages.forSaleFacet[initialParams.forSale]
      )}`;
    }
    delete initialParams.forSale;
  }

  if (initialParams.owner) {
    // TODO Verify correct format
    finalParams.owner = initialParams.owner;
    delete initialParams.owner;
  }

  if (initialParams.rep) {
    // TODO Verify correct format
    finalParams.rep = initialParams.rep;
    delete initialParams.rep;
  }

  if (!isEmpty(initialParams.group)) {
    const group = initialParams.group.toLowerCase();
    const finalGroup = messages.groupRoutes[group]
      ? tr(messages.groupRoutes[group])
      : group;
    finalParams.group = `${config.SEARCH_URL_ITEM_CLASSGROUP}-${finalGroup}`;
    delete initialParams.group;
  }

  finalParams = updateLocationPathParams(finalParams);

  for (let key in initialParams) {
    if (
      initialParams[key] !== bt.DEFAULT_REFINE_SEARCH[key] &&
      initialParams[key] &&
      !(
        typeof bt.DEFAULT_REFINE_SEARCH[key] === 'object' &&
        isEmpty(bt.DEFAULT_REFINE_SEARCH[key])
      )
    ) {
      const translatedKey = has(messages.search, key)
        ? tr(messages.search[key])
        : key.charAt(0).toLowerCase() + key.slice(1);
      finalParams[key] = `${translatedKey}-${initialParams[key]}`;
    }
  }

  // NOTE: To generate the URL we need to encode the value for each search parameter, but to accept it we don't ¯\_(⊙︿⊙)_/¯
  // NOTE: Maybe someone doesn't understand the previous note, left it here for posterity ¯\_(⊙︿⊙)_/¯
  let searchPattern = bt.GENERATE_SEARCH_URL_PATTERN;
  if (finalParams.owner) {
    const dealerStr = tr(messages.dealerPrefix);
    searchPattern = getDealerGalleryPattern(
      getConfig(),
      bt,
      finalParams.owner,
      dealerStr
    );
  }
  if (removeLastSlash) {
    return generatePath(searchPattern, finalParams).slice(0, -1);
  }

  return generatePath(searchPattern, finalParams);
};


